import {
  localizedModifierGroupSchema,
  modifierDeleteResponseSchema,
  modifierGroupRequiredResponseSchema,
  modifierGroupResponseSchema,
  modifierOptionResponseSchema,
  type ListResult,
  type LocalizedModifierGroup,
  type LocalizedText,
  type ModifierDeleteResult,
  type ModifierGroupWriteResult,
  type ModifierOptionWriteResult,
  type PaginationParams,
  type ProductStatus,
} from '../model';
import {
  deleteOneParsed,
  getList,
  getOne,
  patchOne,
  postOne,
  putVoid,
  type RequestOptions,
} from './http';

// Modifier Group catalogue — NEW surface in API v1.00.227. Groups stopped being
// product-scoped: they are now merchant-wide catalogue entities
// (`/merchant/modifier-groups`, `/merchant/modifier-options/{id}`) that products LINK to, so one
// "Соусы" group can serve every dish that carries sauces, and adding an option
// to it surfaces in all of them at once. The old product-scoped routes
// (`PUT /products/{pid}/modifier-groups/{gid}`, `POST|PUT|DELETE
// .../options[/{oid}]`) are DELETED from the spec — calling them 404s.
//
// Every write is moderation-gated like the rest of the merchant surface, so
// each answers with the entity OR a `pending_change` receipt — never assume the
// entity is present. Deletes are parsed for the same receipt.

/**
 * One restaurant's STARTING state for an option being created
 * (`v1ModifierOptionRestaurantStateInput`). Unlike the patch shape there are no
 * clear flags — there is nothing yet to clear. `stock_quantity` omitted = this
 * branch keeps no count (untracked, always available); `0` is a real value
 * meaning counted and none left. `price` absent = the branch charges the
 * catalogue price.
 */
export interface ModifierOptionRestaurantStateInput {
  restaurant_id: string;
  stock_quantity?: number;
  status?: ProductStatus;
  /** This branch's price for the option, in UZS so'm. */
  price?: number;
}

/**
 * `v1ModifierOptionInput` — one option, as submitted inline with a group create
 * or appended via `POST /merchant/modifier-groups/{group_id}/options`.
 *
 * `name` is `LocalizedText`, so a group's initial batch of options gets full
 * ru/uz/en names at creation time — no follow-up translation pass needed.
 */
export interface ModifierOptionInput {
  name: LocalizedText;
  /** UZS so'm, ADDED to the product's price. */
  price?: number;
  min_amount?: number;
  /** `0` defaults to 1 server-side. */
  max_amount?: number;
  default_selected?: boolean;
  /** Omitted, the option lands at the END of the group. */
  sort_order?: number;
  /** Per-option OFD classifiers (an option can be its own fiscalized good). */
  ikpu_code?: string;
  unit_code?: string;
  has_marked_code?: boolean;
  marked_code?: string;
  /** VAT %; omitted = inherit, `0` = no VAT. */
  vat_percent?: number;
  /** Per-restaurant starting state; empty = untracked and available everywhere. */
  restaurant_states?: ModifierOptionRestaurantStateInput[];
}

/**
 * `v1CreateModifierGroupRequest` — a catalogue group and, optionally, the
 * options it starts with.
 *
 * `restaurant_id` is NOT the group's owner — groups are merchant-wide — but the
 * branch the merchant is working in, which is what lets the moderation queue's
 * restaurant filter find the request.
 */
export interface CreateModifierGroupInput {
  name: LocalizedText;
  /** `0` (or omitted) makes the group optional for the customer. */
  min_select?: number;
  /** How many the customer may pick; OMIT for no ceiling (`1` = radio group). */
  max_select?: number;
  /** 1..50 when set; OMIT for no group-specific cap (`0` is rejected). */
  max_options?: number;
  options?: ModifierOptionInput[];
  restaurant_id?: string;
}

/**
 * `ProductServiceUpdateModifierGroupBody` — a PATCH: an absent field is left
 * alone. The `max_select`/`max_options` ceilings are DROPPED via their
 * `clear_*` flag, not by sending 0 — `0` is rejected (a separate flag rather
 * than an empty wrapper because Kong's JSON→protobuf bridge drops empty
 * objects on the way in). `max_options` smaller than the group's current
 * option count is rejected.
 */
export interface UpdateModifierGroupInput {
  name?: LocalizedText;
  min_select?: number;
  max_select?: number;
  clear_max_select?: boolean;
  max_options?: number;
  clear_max_options?: boolean;
  /** Moderation-queue branch filter, same role as on the create. */
  restaurant_id?: string;
}

/**
 * `ProductServiceUpdateModifierOptionBody` — every field `nil = don't touch`,
 * `name` included, so a partial patch touches only what it names. Branch-local
 * state (stock/price per restaurant) is NOT here — that goes through
 * `modifier_option_states` on `PATCH /merchant/products/{id}/restaurant-products`.
 */
export type UpdateModifierOptionInput = Partial<
  Omit<ModifierOptionInput, 'restaurant_states'>
> & {
  restaurant_id?: string;
};

/** Filters for `GET /merchant/modifier-groups` — the merchant's whole group catalogue. */
export type ListModifierGroupsParams = PaginationParams;

export const modifierApi = {
  /**
   * The merchant's group catalogue — `GET /merchant/modifier-groups`. Token-scoped (no
   * merchant or restaurant argument), answering localized groups with their
   * options inline.
   */
  list(
    params?: ListModifierGroupsParams,
    options?: RequestOptions,
  ): Promise<ListResult<LocalizedModifierGroup>> {
    return getList(
      'merchant/modifier-groups',
      localizedModifierGroupSchema,
      params,
      options,
    );
  },

  /** One catalogue group by id, with its options — `GET /merchant/modifier-groups/{id}`. */
  async get(groupId: string, options?: RequestOptions): Promise<LocalizedModifierGroup> {
    const { group } = await getOne(
      `merchant/modifier-groups/${groupId}`,
      modifierGroupRequiredResponseSchema,
      undefined,
      options,
    );
    return group;
  },

  /**
   * Create a catalogue group (optionally with its first options) — `POST
   * /modifier-groups`. The group exists on its own; putting it on a dish is a
   * separate ATTACH (`productApi.attachModifierGroup`), unless the dish write
   * carried it inline as a `new_group`.
   */
  create(body: CreateModifierGroupInput): Promise<ModifierGroupWriteResult> {
    return postOne('merchant/modifier-groups', body, modifierGroupResponseSchema);
  },

  /** Patch a group's own fields — `PATCH /merchant/modifier-groups/{id}`. */
  update(
    groupId: string,
    body: UpdateModifierGroupInput,
  ): Promise<ModifierGroupWriteResult> {
    return patchOne(
      `merchant/modifier-groups/${groupId}`,
      body,
      modifierGroupResponseSchema,
    );
  },

  /**
   * Delete a catalogue group and its options everywhere — `DELETE
   * /modifier-groups/{id}`. NOT the same as taking it off one dish (that is
   * `productApi.detachModifierGroup`, which the group survives).
   */
  remove(groupId: string): Promise<ModifierDeleteResult> {
    return deleteOneParsed(`modifier-groups/${groupId}`, modifierDeleteResponseSchema);
  },

  /**
   * Append one option to a group — `POST /merchant/modifier-groups/{id}/options`. The
   * option is wrapped in `option`, not flat, and immediately appears in every
   * dish that carries the group. Without `sort_order` it lands at the end.
   */
  addOption(
    groupId: string,
    option: ModifierOptionInput,
    restaurantId?: string,
  ): Promise<ModifierOptionWriteResult> {
    return postOne(
      `merchant/modifier-groups/${groupId}/options`,
      { option, ...(restaurantId ? { restaurant_id: restaurantId } : {}) },
      modifierOptionResponseSchema,
    );
  },

  /**
   * Patch one option in place — `PATCH /merchant/modifier-options/{id}`. Options are
   * addressed by their OWN id now; the group no longer appears in the path.
   */
  updateOption(
    optionId: string,
    body: UpdateModifierOptionInput,
  ): Promise<ModifierOptionWriteResult> {
    return patchOne(
      `merchant/modifier-options/${optionId}`,
      body,
      modifierOptionResponseSchema,
    );
  },

  /** Remove one option from its group — `DELETE /merchant/modifier-options/{id}`. */
  removeOption(optionId: string): Promise<ModifierDeleteResult> {
    return deleteOneParsed(
      `merchant/modifier-options/${optionId}`,
      modifierDeleteResponseSchema,
    );
  },

  /**
   * Hide or show a group at ONE branch — `PUT
   * /modifier-groups/{id}/restaurants/{restaurant_id}/visibility`. Idempotent
   * (it states the desired state, not a delta). Hiding takes the group and its
   * options off that branch's reads, and an order naming it there is rejected.
   * Per group and per branch, never per dish: a branch either offers the
   * section or it does not.
   */
  setRestaurantVisibility(
    groupId: string,
    restaurantId: string,
    hidden: boolean,
  ): Promise<void> {
    return putVoid(
      `merchant/modifier-groups/${groupId}/restaurants/${restaurantId}/visibility`,
      {
        hidden,
      },
    );
  },
};
